import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, GraduationCap, ExternalLink } from "lucide-react";
import { INITIATIVES } from "@/data/content";
import { Reveal } from "@/components/Reveal";

export const InitiativesSpotlight = () => {
  const a = INITIATIVES.academy;
  const k = INITIATIVES.aiilm;

  return (
    <section data-testid="initiatives-spotlight" className="relative py-24 md:py-32 border-t border-stone-200 bg-white overflow-hidden">
      <div className="absolute -top-32 right-1/4 w-[600px] h-[400px] glow-amber pointer-events-none opacity-70" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-4">
                04 — ITHR Initiatives
              </div>
              <h2 className="font-display text-stone-900 text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl">
                We don't just consult on AI.<br />
                <span className="text-stone-500">We build the ground others stand on.</span>
              </h2>
            </div>
            <Link
              to="/initiatives"
              data-testid="initiatives-spotlight-more"
              className="inline-flex items-center gap-2 text-[#B8860B] hover:text-stone-900 link-underline"
            >
              Explore both initiatives
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Academy — large card */}
          <Reveal className="bento-card lg:col-span-3 p-8 md:p-12 flex flex-col" data-testid="spotlight-academy">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#B8860B]" />
              <span className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold">
                Flagship · Enterprise
              </span>
            </div>
            <h3 className="font-display text-stone-900 text-3xl md:text-4xl font-light tracking-tighter mb-4">
              {a.name}
            </h3>
            <p className="font-display italic text-stone-700 text-lg md:text-xl mb-6 leading-snug">
              {a.short}
            </p>
            <p className="text-stone-600 leading-relaxed mb-8 flex-1">
              A credentialing platform for enterprise workforces — ten tiers, twenty industries, one standard, refreshed continuously. From Foundation to Chief AI Officer, learn with our Socratic AI tutor and prove your competency with cryptographically-verifiable credentials.
            </p>

            <div className="grid grid-cols-4 gap-4 mb-8 border-t border-stone-200 pt-6">
              {a.stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-xl md:text-2xl text-stone-900 font-light">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                data-testid="spotlight-academy-cta"
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#B8860B] text-white text-sm font-medium hover:bg-stone-900 transition-colors"
              >
                Visit the Academy
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <Link
                to="/initiatives#academy"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-stone-300 text-stone-900 text-sm font-medium hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors"
              >
                Read the write-up
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </Reveal>

          {/* AIILM — smaller card */}
          <Reveal className="bento-card lg:col-span-2 p-8 md:p-10 flex flex-col relative overflow-hidden" data-testid="spotlight-aiilm">
            <div className="absolute top-6 right-6 inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.15em] bg-emerald-50 text-emerald-800 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live
            </div>

            <GraduationCap className="w-10 h-10 text-[#B8860B] mb-6" strokeWidth={1.25} />
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8860B] font-semibold mb-3">
              Society · Kids & Schools
            </div>
            <h3 className="font-display text-stone-900 text-3xl md:text-4xl font-light tracking-tighter mb-4">
              {k.name}
            </h3>
            <p className="font-display italic text-stone-700 text-lg mb-6 leading-snug">
              {k.short}
            </p>
            <p className="text-stone-600 leading-relaxed mb-8 flex-1">
              Kids teach machines, run simulations and build AI agents — guided by Sparky, their AI buddy. Bilingual (English & Arabic), GCC-rooted, ages 10–16.
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href={k.url}
                target="_blank"
                rel="noreferrer"
                data-testid="spotlight-aiilm-visit"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#B8860B] text-white text-sm font-medium hover:bg-stone-900 transition-colors"
              >
                Visit aiilm.me
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <Link
                to="/initiatives#aiilm"
                data-testid="spotlight-aiilm-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-stone-300 text-stone-900 text-sm font-medium hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors"
              >
                Read more
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default InitiativesSpotlight;
